import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Textarea,
  Text,
  VStack,
  HStack,
  Badge
} from '@chakra-ui/react'
import { FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { getShortPedidoId } from '@helper/mrp/approval'

import {
  IApprovalFormData,
  IPendingOrderItem
} from '@base/@types/providers/Mrp/ApprovalProvider'

import FalhasAlert from './FalhasAlert'

interface ApproveDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (data: IApprovalFormData) => Promise<boolean | void>
  pedidoId: string
  fornecedor: string
  aprovar: boolean
  itensFalhos?: IPendingOrderItem[]
}

interface FormData {
  observacao: string
}

const ApproveDialog: FC<ApproveDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  pedidoId,
  fornecedor,
  aprovar,
  itensFalhos = []
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [cienteDosFalhos, setCienteDosFalhos] = useState(false)
  const temFalhas = itensFalhos.length > 0

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormData>()

  // A ciência das falhas vale só para o pedido/ação atual
  useEffect(() => {
    setCienteDosFalhos(false)
  }, [isOpen, pedidoId, aprovar])

  const onSubmit = async (data: FormData) => {
    if (aprovar && temFalhas && !cienteDosFalhos) return

    setIsSubmitting(true)

    try {
      const result = await onConfirm({
        aprovar,
        // Valor esperado pelo backend: 'Aprovado' | 'Recusado'
        justificativa: aprovar ? 'Aprovado' : 'Recusado',
        observacao: data.observacao || '',
        enviadoComFalha: temFalhas && cienteDosFalhos
      })
      // No erro (false) o diálogo fica aberto com a observação digitada
      if (result !== false) {
        reset()
        onClose()
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      reset()
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOverlayClick={!isSubmitting}
      size="md"
    >
      <ModalOverlay />
      <ModalContent bg="white">
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            <HStack>
              <Text>{aprovar ? 'Aprovar' : 'Rejeitar'} Pedido</Text>
              <Badge colorScheme={aprovar ? 'green' : 'red'}>
                {aprovar ? 'APROVAÇÃO' : 'REJEIÇÃO'}
              </Badge>
            </HStack>
          </ModalHeader>

          <ModalCloseButton isDisabled={isSubmitting} />

          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                <strong>Pedido:</strong> {getShortPedidoId(pedidoId)}
              </Text>

              <Text fontSize="sm" color="gray.600">
                <strong>Fornecedor:</strong> {fornecedor}
              </Text>

              <Text fontSize="sm" color="gray.700" mb={4}>
                <strong>Ação:</strong> Este pedido será{' '}
                {aprovar ? 'aprovado' : 'rejeitado'}
              </Text>

              <FormControl isInvalid={!!errors.observacao}>
                <FormLabel>Observação (opcional)</FormLabel>
                <Textarea
                  {...register('observacao', {
                    maxLength: {
                      value: 500,
                      message: 'Observação deve ter no máximo 500 caracteres'
                    }
                  })}
                  placeholder="Digite observações adicionais (opcional)..."
                  rows={4}
                  isDisabled={isSubmitting}
                />
                <FormErrorMessage>
                  {errors.observacao?.message}
                </FormErrorMessage>
              </FormControl>

              {temFalhas && (
                <FalhasAlert
                  itens={itensFalhos}
                  isChecked={cienteDosFalhos}
                  onChange={setCienteDosFalhos}
                  isDisabled={isSubmitting}
                />
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={handleClose}
              isDisabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              colorScheme={aprovar ? 'green' : 'red'}
              isLoading={isSubmitting}
              loadingText={aprovar ? 'Aprovando...' : 'Rejeitando...'}
              isDisabled={aprovar && temFalhas && !cienteDosFalhos}
            >
              {aprovar ? 'Aprovar' : 'Rejeitar'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

export default ApproveDialog
